/**
 * MIM On-Call Dashboard
 * Major Incident Management — on-call visibility across all Hedwig-onboarded applications
 * Data source: /api/hedwig/data (same as PortfolioHealthTracker)
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import { API_BASE_URL } from '../config/api';

// ─── Raw API types ────────────────────────────────────────────────────────────

interface RawOnCallUserDetail {
  emailId?: string | null;
  userName?: string | null;
  serviceId?: string;
  serviceName?: string;
}

interface RawServiceFull {
  id?: string;
  name: string;
  defaultService?: boolean;
  description?: string;
  onCallUsers?: number;
  onCallUserDetails?: RawOnCallUserDetail[];
}

interface RawServiceData {
  serviceName?: string | null;
  onCallUsers: string[];
  full_data?: RawServiceFull;
}

interface RawTeamData {
  teamId: string;
  teamName?: string | null;
}

interface RawAppData {
  api_response_status: string;
  app_id: string;
  app_name?: string;
  mvc_group?: string;
  bca_status?: string;
  ops_status?: string;
  has_hedwig_team: boolean;
  last_updated?: string;
  service_count: number;
  team_count: number;
  team_names?: string;
  teams_data: RawTeamData[];
  services_data: RawServiceData[];
  oncall_users_data?: string[];
  unique_oncall_users?: number;
  hedwig_platforms?: string;
  hedwig_portfolios?: string;
}

// ─── Internal types ──────────────────────────────────────────────────────────

interface ServiceUser {
  name: string;
  email: string;
}

interface Service {
  name: string;
  users: ServiceUser[];
}

interface AppEntry {
  app_id: string;
  app_name: string;
  mvc_group: string;
  bca_status: string;
  ops_status: string;
  portfolio: string;
  platform: string;
  has_team: boolean;
  teams: Array<{ name: string; id: string }>;
  services: Service[];
}

type AppStatus = 'ready' | 'no_users' | 'no_service' | 'no_team';

interface ProcessedApp extends AppEntry {
  status: AppStatus;
}

// ─── Transform raw API response → internal format ────────────────────────────

function transformRaw(raw: RawAppData): AppEntry {
  const teams = (raw.teams_data ?? []).map(t => ({ name: t.teamName ?? '', id: t.teamId }));
  const services: Service[] = (raw.services_data ?? []).map(s => {
    const users: ServiceUser[] = (s.full_data?.onCallUserDetails ?? []).map(u => ({
      name: u.userName ?? '',
      email: u.emailId ?? '',
    }));
    return { name: s.serviceName ?? '', users };
  });
  return { app_id: raw.app_id, app_name: raw.app_name ?? '', mvc_group: raw.mvc_group ?? '', bca_status: raw.bca_status ?? '', ops_status: raw.ops_status ?? '', portfolio: raw.hedwig_portfolios ?? '', platform: raw.hedwig_platforms ?? '', has_team: raw.has_hedwig_team, teams, services };
}

// ─── Status derivation ───────────────────────────────────────────────────────

function deriveStatus(app: AppEntry): AppStatus {
  if (!app.has_team) return 'no_team';
  if (app.services.length === 0) return 'no_service';
  const hasAnyUsers = app.services.some(s => s.users.length > 0);
  return hasAnyUsers ? 'ready' : 'no_users';
}

// ─── Status config ───────────────────────────────────────────────────────────

const STATUS_CFG: Record<
  AppStatus,
  { label: string; border: string; badgeBg: string; badgeText: string; alertBg: string; alertBorder: string }
> = {
  ready: { label: 'On-Call Ready', border: '#16a34a', badgeBg: '#dcfce7', badgeText: '#15803d', alertBg: '#f0fdf4', alertBorder: '#86efac' },
  no_users: { label: 'No On-Call Users', border: '#d97706', badgeBg: '#fef3c7', badgeText: '#b45309', alertBg: '#fffbeb', alertBorder: '#fde68a' },
  no_service: { label: 'No Service', border: '#d97706', badgeBg: '#fef3c7', badgeText: '#b45309', alertBg: '#fffbeb', alertBorder: '#fde68a' },
  no_team: { label: 'Not in Hedwig', border: '#dc2626', badgeBg: '#fee2e2', badgeText: '#b91c1c', alertBg: '#fef2f2', alertBorder: '#fca5a5' },
};

const GAP_DESCRIPTION: Record<AppStatus, React.ReactNode> = {
  ready: null,
  no_users: (
    <span>
      ⚠️ Services are configured but <strong>no on-call users are assigned</strong> to any service.{' '}
      <a href="https://hedwig.maersk.io/" target="_blank" rel="noopener noreferrer"
        style={{ color: '#b45309', fontWeight: 700, textDecoration: 'underline' }}>
        Set up On-Call schedules in Hedwig ↗
      </a>
    </span>
  ),
  no_service: (
    <span>
      ⚠️ Team is onboarded to Hedwig but has <strong>no On-Call service configured</strong> yet.{' '}
      <a href="https://hedwig.maersk.io/" target="_blank" rel="noopener noreferrer"
        style={{ color: '#b45309', fontWeight: 700, textDecoration: 'underline' }}>
        Create an On-Call Service in Hedwig ↗
      </a>
    </span>
  ),
  no_team: null, // rendered inline in AppCard to inject the real app_id
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0]?.toUpperCase() ?? '')
    .join('');
}

function dedupeUsers(services: Service[]): ServiceUser[] {
  const seen = new Set<string>();
  const result: ServiceUser[] = [];
  for (const svc of services) {
    for (const user of svc.users) {
      if (!seen.has(user.email)) {
        seen.add(user.email);
        result.push(user);
      }
    }
  }
  return result;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: AppStatus }> = ({ status }) => {
  const cfg = STATUS_CFG[status];
  return (
    <span
      style={{
        backgroundColor: cfg.badgeBg,
        color: cfg.badgeText,
        border: `1px solid ${cfg.border}`,
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.03em',
      }}
    >
      {cfg.label}
    </span>
  );
};

const Avatar: React.FC<{ name: string }> = ({ name }) => (
  <div
    style={{
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: '#0067b3',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: 700,
      flexShrink: 0,
      userSelect: 'none',
    }}
  >
    {getInitials(name)}
  </div>
);

const CopyButton: React.FC<{ onClick: () => void; title?: string }> = ({ onClick, title }) => (
  <button
    onClick={onClick}
    title={title ?? 'Copy'}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '2px 4px',
      color: '#94a3b8',
      flexShrink: 0,
      borderRadius: '4px',
      lineHeight: 1,
    }}
    onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f1f5f9')}
    onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')}
  >
    📋
  </button>
);

// ─── App card ────────────────────────────────────────────────────────────────

const AppCard: React.FC<{ app: ProcessedApp; onCopy: (text: string, msg: string) => void }> = ({
  app,
  onCopy,
}) => {
  const cfg = STATUS_CFG[app.status];
  const allUniqueUsers = dedupeUsers(app.services);
  const allEmails = allUniqueUsers.map(u => u.email);

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderLeft: `4px solid ${cfg.border}`,
        borderRadius: '10px',
        padding: '16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        {/* Left: app_id badge + status on first row, then name + MVC below */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 }}>
          {/* Row 1: app_id + status badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
            <span
              style={{
                backgroundColor: '#1e293b',
                color: '#fff',
                padding: '3px 10px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.05em',
                display: 'inline-block',
              }}
            >
              {app.app_id}
            </span>
            <StatusBadge status={app.status} />
          </div>

          {/* Row 2: app name + MVC badge inline */}
          {app.app_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155', lineHeight: 1.3 }}>
                {app.app_name}
              </span>
              {app.mvc_group && (
                <span
                  style={{
                    backgroundColor: '#f0f9ff',
                    color: '#0369a1',
                    border: '1px solid #7dd3fc',
                    padding: '1px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  MVC
                </span>
              )}
              {app.bca_status && (
                <span
                  style={{
                    backgroundColor: '#f5f3ff',
                    color: '#7c3aed',
                    border: '1px solid #c4b5fd',
                    padding: '1px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  BCA
                </span>
              )}
              {app.ops_status && (
                <span
                  style={{
                    backgroundColor: '#fffbeb',
                    color: '#b45309',
                    border: '1px solid #fde68a',
                    padding: '1px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  OPS
                </span>
              )}
            </div>
          )}

          {/* Row 3: portfolio > platform breadcrumb */}
          {(app.portfolio || app.platform) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              {app.portfolio && <span style={{ fontSize: '11px', color: '#94a3b8' }}>{app.portfolio}</span>}
              {app.portfolio && app.platform && <span style={{ fontSize: '10px', color: '#cbd5e1' }}>›</span>}
              {app.platform && <span style={{ fontSize: '11px', color: '#94a3b8' }}>{app.platform}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Teams */}
      {app.teams.length > 0 && (
        <div>
          <div style={sectionLabel}>Teams</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {app.teams.map((t, i) => (
              <a
                key={t.id}
                href={`https://hedwig.maersk.io/teams/${t.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={i === 0 ? teamChipPrimary : teamChipSecondary}
              >
                {t.name} ↗
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Gap description (non-ready) */}
      {app.status !== 'ready' && (
        <div
          style={{
            backgroundColor: cfg.alertBg,
            border: `1px solid ${cfg.alertBorder}`,
            borderRadius: '6px',
            padding: '8px 10px',
            fontSize: '12px',
            color: cfg.badgeText,
            lineHeight: 1.5,
          }}
        >
          {app.status === 'no_team' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontWeight: 700, fontSize: '12px', color: '#b91c1c', marginBottom: '2px' }}>
                🔴 No Hedwig team found for <strong>{app.app_id}</strong>. This could be due to either of the following reasons:
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 10px' }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>1️⃣</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#7f1d1d', marginBottom: '3px' }}>Application not onboarded to Hedwig</div>
                  <div style={{ fontSize: '11px', color: '#991b1b', lineHeight: 1.5 }}>
                    This application has no Hedwig team created yet.{' '}
                    <a href="https://hedwig.maersk.io/" target="_blank" rel="noopener noreferrer" style={{ color: '#b91c1c', fontWeight: 700, textDecoration: 'underline' }}>Onboard your team to Hedwig ↗</a>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 10px' }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>2️⃣</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#7f1d1d', marginBottom: '3px' }}>MAC App ID not linked in Hedwig scope</div>
                  <div style={{ fontSize: '11px', color: '#991b1b', lineHeight: 1.5 }}>
                    Your Hedwig team may already exist but MAC App ID <strong>{app.app_id}</strong> is not mapped to the Hedwig team scope.{' '}
                    <a href="https://hedwig.maersk.io/" target="_blank" rel="noopener noreferrer" style={{ color: '#b91c1c', fontWeight: 700, textDecoration: 'underline' }}>Link MAC App ID in Hedwig ↗</a>
                  </div>
                </div>
              </div>
            </div>
          ) : GAP_DESCRIPTION[app.status]}
        </div>
      )}

      {/* Services */}
      {app.services.length > 0 && (
        <div>
          <div style={sectionLabel}>Services</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {app.services.map(svc => (
              <div
                key={svc.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#f8fafc',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '12px', color: '#334155', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {svc.name}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 7px',
                    borderRadius: '999px',
                    flexShrink: 0,
                    backgroundColor: svc.users.length > 0 ? '#dcfce7' : '#fee2e2',
                    color: svc.users.length > 0 ? '#15803d' : '#b91c1c',
                  }}
                >
                  {svc.users.length > 0
                    ? `${svc.users.length} user${svc.users.length > 1 ? 's' : ''}`
                    : 'no users'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* On-call contacts */}
      {app.status === 'ready' && allUniqueUsers.length > 0 && (
        <div>
          <div style={sectionLabel}>On-Call Contacts</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {allUniqueUsers.map(user => (
              <div key={user.email} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar name={user.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.email}
                  </div>
                </div>
                <CopyButton
                  onClick={() => onCopy(user.email, `Copied ${user.email}`)}
                  title="Copy email"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copy all emails for this app */}
      {app.status === 'ready' && allEmails.length > 0 && (
        <button
          onClick={() => onCopy(allEmails.join(', '), `Copied ${allEmails.length} email(s) for ${app.app_id}`)}
          style={{
            marginTop: 'auto',
            padding: '7px 12px',
            backgroundColor: '#f0fdf4',
            color: '#15803d',
            border: '1px solid #86efac',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          📋 Copy all emails for {app.app_id}
        </button>
      )}
    </div>
  );
};

// ─── Shared micro-styles ─────────────────────────────────────────────────────

const sectionLabel: React.CSSProperties = {
  fontSize: '11px',
  color: '#94a3b8',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  marginBottom: '6px',
};

const teamChipPrimary: React.CSSProperties = {
  backgroundColor: '#f1f5f9',
  color: '#334155',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  textDecoration: 'none',
  display: 'inline-block',
};

const teamChipSecondary: React.CSSProperties = {
  backgroundColor: '#e2e8f0',
  color: '#64748b',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '11px',
  textDecoration: 'none',
  display: 'inline-block',
};

// ─── Pagination helpers ──────────────────────────────────────────────────────

const PAGE_SIZE = 12;

function getPagesToShow(current: number, total: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || Math.abs(p - current) <= 2) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== 'ellipsis') {
      pages.push('ellipsis');
    }
  }
  return pages;
}

function pagerBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: disabled ? '#f8fafc' : '#fff',
    color: disabled ? '#cbd5e1' : '#334155',
  };
}

// ─── Main component ──────────────────────────────────────────────────────────

const MIMOnCallDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apps, setApps] = useState<ProcessedApp[]>([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<AppStatus | 'warning' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState<'' | 'mvc' | 'bca' | 'ops'>('');
  const [filterPortfolio, setFilterPortfolio] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterApp, setFilterApp] = useState('');

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`${API_BASE_URL}/api/hedwig/data`);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const result = await response.json();
        const payload: RawAppData[] = result?.data ?? result;
        const normalized = Array.isArray(payload) ? payload : payload ? [payload] : [];
        if (!cancelled) {
          const processed: ProcessedApp[] = normalized.map(raw => {
            const entry = transformRaw(raw);
            return { ...entry, status: deriveStatus(entry) };
          });
          setApps(processed);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load Hedwig data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Reset to page 1 whenever search, status filter, or toggle changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterStatus, filterCategory, filterPortfolio, filterPlatform, filterApp]);

  // ─── Toast ──────────────────────────────────────────────────────────────────

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleCopy = (text: string, msg: string) => {
    navigator.clipboard.writeText(text).then(() => showToast(msg)).catch(() => showToast('Copy failed'));
  };

  // ─── Counts ─────────────────────────────────────────────────────────────────
  // (computed below after MVC filter is applied)

  // ─── Filtering ──────────────────────────────────────────────────────────────

  const baseApps = filterCategory === 'mvc'
    ? apps.filter(a => a.mvc_group !== '')
    : filterCategory === 'bca'
      ? apps.filter(a => a.bca_status !== '' && a.mvc_group === '')
      : filterCategory === 'ops'
        ? apps.filter(a => a.ops_status !== '')
        : apps;

  // ── Cascading dropdown options (Portfolio → Platform → Application) ──────────
  // All derived from baseApps (MVC-filtered only), cascading on each prior selection
  const portfolioOptions: string[] = [];
  const _portfolioSeen = new Set<string>();
  for (const a of baseApps) {
    if (a.portfolio && !_portfolioSeen.has(a.portfolio)) { _portfolioSeen.add(a.portfolio); portfolioOptions.push(a.portfolio); }
  }
  portfolioOptions.sort();

  const platformBase = filterPortfolio ? baseApps.filter(a => a.portfolio === filterPortfolio) : baseApps;
  const platformOptions: string[] = [];
  const _platformSeen = new Set<string>();
  for (const a of platformBase) {
    if (a.platform && !_platformSeen.has(a.platform)) { _platformSeen.add(a.platform); platformOptions.push(a.platform); }
  }
  platformOptions.sort();

  const appFilterBase = filterPlatform ? platformBase.filter(a => a.platform === filterPlatform) : platformBase;
  const appOptions: Array<{ id: string; name: string }> = [];
  const _appSeen = new Set<string>();
  for (const a of appFilterBase) {
    if (!_appSeen.has(a.app_id)) { _appSeen.add(a.app_id); appOptions.push({ id: a.app_id, name: a.app_name || a.app_id }); }
  }
  appOptions.sort((a, b) => a.name.localeCompare(b.name));

  // scopedApps = baseApps narrowed by dropdown selections only (no status/search)
  // This drives flash-card counts so they always reflect what the user has scoped
  const scopedApps = baseApps.filter(app => {
    if (filterPortfolio && app.portfolio !== filterPortfolio) return false;
    if (filterPlatform && app.platform !== filterPlatform) return false;
    if (filterApp && app.app_id !== filterApp) return false;
    return true;
  });

  // Summary counts reflect the current scope (MVC + dropdown selections)
  const displayCounts: Record<AppStatus, number> = {
    ready: scopedApps.filter(a => a.status === 'ready').length,
    no_users: scopedApps.filter(a => a.status === 'no_users').length,
    no_service: scopedApps.filter(a => a.status === 'no_service').length,
    no_team: scopedApps.filter(a => a.status === 'no_team').length,
  };
  const displayTotal = scopedApps.length;

  const searchLower = search.toLowerCase();
  const filteredApps = scopedApps.filter(app => {
    if (filterStatus === 'warning' && app.status !== 'no_service' && app.status !== 'no_users') return false;
    if (filterStatus && filterStatus !== 'warning' && app.status !== filterStatus) return false;
    if (!search) return true;
    if (app.app_id.toLowerCase().includes(searchLower)) return true;
    if (app.app_name.toLowerCase().includes(searchLower)) return true;
    if (app.portfolio.toLowerCase().includes(searchLower)) return true;
    if (app.platform.toLowerCase().includes(searchLower)) return true;
    if (app.teams.some(t => t.name.toLowerCase().includes(searchLower))) return true;
    if (
      app.services.some(
        s =>
          s.name.toLowerCase().includes(searchLower) ||
          s.users.some(
            u =>
              u.name.toLowerCase().includes(searchLower) ||
              u.email.toLowerCase().includes(searchLower),
          ),
      )
    )
      return true;
    return false;
  });

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / PAGE_SIZE));
  const pagedApps = filteredApps.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ─── CSV export ──────────────────────────────────────────────────────────

  const exportToCsv = () => {
    const exportApps = scopedApps; // respects all scope filter selections (portfolio, platform, app, category)
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;

    const headers = [
      'App ID',
      'App Name',
      'Portfolio',
      'Platform',
      'MVC',
      'Status',
      'Teams',
      'Team Hedwig Links',
      'Services',
      'On-Call Users (name | email)',
      'All On-Call Emails',
    ];

    const rows = exportApps.map(app => {
      const statusLabel = STATUS_CFG[app.status].label;
      const teams = app.teams.map(t => t.name).join('; ');
      const teamLinks = app.teams.map(t => `https://hedwig.maersk.io/teams/${t.id}`).join('; ');
      const services = app.services.map(s => s.name).join('; ');
      const uniqueUsers = dedupeUsers(app.services);
      const contacts = uniqueUsers.map(u => `${u.name} | ${u.email}`).join('; ');
      const emails = uniqueUsers.map(u => u.email).join('; ');
      return [
        escape(app.app_id),
        escape(app.app_name),
        escape(app.portfolio),
        escape(app.platform),
        escape(app.mvc_group ? 'Yes' : 'No'),
        escape(statusLabel),
        escape(teams),
        escape(teamLinks),
        escape(services),
        escape(contacts),
        escape(emails),
      ].join(',');
    });

    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const suffix = filterCategory || 'all';
    link.href = url;
    link.download = `mim_oncall${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${exportApps.length} app${exportApps.length !== 1 ? 's' : ''} to CSV`);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        padding: '24px',
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      {/* ── Page Header ── */}
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#001a35' }}>
            Hedwig On-Call Dashboard
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={exportToCsv}
            style={{
              padding: '8px 16px',
              backgroundColor: '#0067b3',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ⬇️ Export CSV
          </button>
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '80px 24px', color: '#64748b', fontSize: '15px' }}>
          ⏳ Loading on-call data…
        </div>
      )}



      {/* ── Error ── */}
      {error && (
        <div
          style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            padding: '16px',
            color: '#991b1b',
            marginBottom: '24px',
            fontSize: '13px',
          }}
        >
          ⚠️ {error}
        </div>
      )}

      {/* ── Main content ── */}
      {!loading && !error && (
        <>
          {/* ── Portfolio / Platform / Application cascading filters ── */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>🔎 Scope Filter</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Portfolio</label>
                <select
                  value={filterPortfolio}
                  onChange={e => { setFilterPortfolio(e.target.value); setFilterPlatform(''); setFilterApp(''); }}
                  style={{ padding: '8px 10px', border: `1.5px solid ${filterPortfolio ? '#0067b3' : '#cbd5e1'}`, borderRadius: '6px', fontSize: '13px', color: filterPortfolio ? '#1e293b' : '#94a3b8', backgroundColor: '#fff', cursor: 'pointer', outline: 'none', fontWeight: filterPortfolio ? 600 : 400 }}
                >
                  <option value="">All Portfolios ({portfolioOptions.length})</option>
                  {portfolioOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Platform</label>
                <select
                  value={filterPlatform}
                  onChange={e => { setFilterPlatform(e.target.value); setFilterApp(''); }}
                  disabled={platformOptions.length === 0}
                  style={{ padding: '8px 10px', border: `1.5px solid ${filterPlatform ? '#0067b3' : '#cbd5e1'}`, borderRadius: '6px', fontSize: '13px', color: filterPlatform ? '#1e293b' : '#94a3b8', backgroundColor: platformOptions.length === 0 ? '#f8fafc' : '#fff', cursor: platformOptions.length === 0 ? 'not-allowed' : 'pointer', outline: 'none', fontWeight: filterPlatform ? 600 : 400 }}
                >
                  <option value="">All Platforms ({platformOptions.length})</option>
                  {platformOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Application</label>
                <select
                  value={filterApp}
                  onChange={e => setFilterApp(e.target.value)}
                  disabled={appOptions.length === 0}
                  style={{ padding: '8px 10px', border: `1.5px solid ${filterApp ? '#0067b3' : '#cbd5e1'}`, borderRadius: '6px', fontSize: '13px', color: filterApp ? '#1e293b' : '#94a3b8', backgroundColor: appOptions.length === 0 ? '#f8fafc' : '#fff', cursor: appOptions.length === 0 ? 'not-allowed' : 'pointer', outline: 'none', fontWeight: filterApp ? 600 : 400 }}
                >
                  <option value="">All Applications ({appOptions.length})</option>
                  {appOptions.map(a => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</label>
                <select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value as '' | 'mvc' | 'bca' | 'ops')}
                  style={{ padding: '8px 10px', border: `1.5px solid ${filterCategory ? '#0067b3' : '#cbd5e1'}`, borderRadius: '6px', fontSize: '13px', color: filterCategory ? '#1e293b' : '#94a3b8', backgroundColor: '#fff', cursor: 'pointer', outline: 'none', fontWeight: filterCategory ? 600 : 400 }}
                >
                  <option value="">All</option>
                  <option value="mvc">Mission Critical</option>
                  <option value="bca">Business Critical</option>
                  <option value="ops">Operational</option>
                </select>
              </div>
              {(filterPortfolio || filterPlatform || filterApp || filterCategory) && (
                <button
                  onClick={() => { setFilterPortfolio(''); setFilterPlatform(''); setFilterApp(''); setFilterCategory(''); }}
                  style={{ padding: '8px 14px', borderRadius: '6px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end' }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
            {(filterPortfolio || filterPlatform || filterApp || filterCategory) && (
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>Scope:</span>
                {filterPortfolio && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: 600 }}>📁 {filterPortfolio}</span>}
                {filterPlatform && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', fontSize: '11px', fontWeight: 600 }}>🏗 {filterPlatform}</span>}
                {filterApp && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#faf5ff', color: '#7e22ce', border: '1px solid #d8b4fe', fontSize: '11px', fontWeight: 600 }}>📱 {appOptions.find(a => a.id === filterApp)?.name ?? filterApp}</span>}
                {filterCategory === 'mvc' && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', fontSize: '11px', fontWeight: 600 }}>🔵 Mission Critical</span>}
                {filterCategory === 'bca' && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd', fontSize: '11px', fontWeight: 600 }}>🟣 Business Critical</span>}
                {filterCategory === 'ops' && <span style={{ padding: '2px 10px', borderRadius: '999px', backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', fontSize: '11px', fontWeight: 600 }}>🟠 Operational</span>}
              </div>
            )}
          </div>

          {/* Summary metric cards — RAG */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '14px',
              marginBottom: '24px',
            }}
          >
            {(
              [
                { label: 'Total Apps', value: displayTotal, color: '#1e293b', bg: '#fff', border: '#e2e8f0', status: null as AppStatus | 'warning' | null },
                { label: '🟢 On-Call Ready', value: displayCounts.ready, color: '#15803d', bg: '#f0fdf4', border: '#86efac', status: 'ready' as AppStatus | 'warning' | null },
                { label: '🟡 Needs Attention', value: displayCounts.no_service + displayCounts.no_users, color: '#b45309', bg: '#fffbeb', border: '#fde68a', status: 'warning' as AppStatus | 'warning' | null },
                { label: '🔴 Not in Hedwig', value: displayCounts.no_team, color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5', status: 'no_team' as AppStatus | 'warning' | null },
              ]
            ).map(card => (
              <div
                key={card.label}
                onClick={() =>
                  setFilterStatus(prev => (prev === card.status ? null : card.status))
                }
                style={{
                  backgroundColor: card.bg,
                  border: `2px solid ${filterStatus === card.status ? card.color : card.border}`,
                  borderRadius: '10px',
                  padding: '16px',
                  cursor: 'pointer',
                  boxShadow:
                    filterStatus === card.status
                      ? `0 0 0 3px ${card.border}`
                      : '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontSize: '28px', fontWeight: 700, color: card.color, lineHeight: 1 }}>
                  {card.value}
                </div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {card.label}
                </div>
              </div>
            ))}
          </div>

          {/* Status legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' }}>
              Status:
            </span>
            {([
              { label: '🟢 On-Call Ready', bg: '#dcfce7', text: '#15803d', border: '#16a34a' },
              { label: '🟡 Needs Attention', bg: '#fef3c7', text: '#b45309', border: '#d97706' },
              { label: '🔴 Not in Hedwig', bg: '#fee2e2', text: '#b91c1c', border: '#dc2626' },
            ]).map(s => (
              <span key={s.label} style={{ padding: '4px 10px', borderRadius: '999px', backgroundColor: s.bg, color: s.text, fontSize: '11px', fontWeight: 600, border: `1px solid ${s.border}` }}>
                {s.label}
              </span>
            ))}
            {(filterStatus !== null || search || filterPortfolio || filterPlatform || filterApp || filterCategory) && (
              <button
                onClick={() => { setFilterStatus(null); setSearch(''); setFilterPortfolio(''); setFilterPlatform(''); setFilterApp(''); setFilterCategory(''); }}
                style={{ padding: '4px 10px', borderRadius: '999px', backgroundColor: '#e2e8f0', color: '#475569', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer', marginLeft: '4px' }}
              >
                ✕ Clear filters
              </button>
            )}
          </div>

          {/* Search */}
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <span
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                pointerEvents: 'none',
                fontSize: '15px',
              }}
            >
              🔍
            </span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by app ID, app name, team, service, user name or email…"
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                color: '#1e293b',
              }}
            />
          </div>

          {/* Results count */}
          <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px', marginTop: 0 }}>
            Showing {filteredApps.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredApps.length)} of {filteredApps.length} application{filteredApps.length !== 1 ? 's' : ''}
            {filterStatus ? ` · Filtered: ${filterStatus === 'warning' ? 'Needs Attention' : STATUS_CFG[filterStatus].label}` : ''}
            {search ? ` · Search: "${search}"` : ''}
          </p>

          {/* Card grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px',
            }}
          >
            {pagedApps.map(app => (
              <AppCard key={app.app_id} app={app} onCopy={handleCopy} />
            ))}
          </div>

          {filteredApps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: '#94a3b8', fontSize: '14px' }}>
              No applications match your current filters.
            </div>
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                marginTop: '32px',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={pagerBtnStyle(currentPage === 1)}
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={pagerBtnStyle(currentPage === 1)}
              >
                ‹ Prev
              </button>

              {getPagesToShow(currentPage, totalPages).map((p, idx) =>
                p === 'ellipsis' ? (
                  <span key={`e-${idx}`} style={{ color: '#94a3b8', fontSize: '13px', padding: '0 4px' }}>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p as number)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: `1px solid ${currentPage === p ? '#0067b3' : '#e2e8f0'}`,
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backgroundColor: currentPage === p ? '#0067b3' : '#fff',
                      color: currentPage === p ? '#fff' : '#334155',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={pagerBtnStyle(currentPage === totalPages)}
              >
                Next ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={pagerBtnStyle(currentPage === totalPages)}
              >
                »
              </button>

              <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                Page {currentPage} of {totalPages} · {filteredApps.length} app{filteredApps.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            backgroundColor: '#1e293b',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          ✅ {toast}
        </div>
      )}
    </div>
  );
};

export default MIMOnCallDashboard;
